import React, { useState, useEffect } from "react";
import {
  FaEnvelope,
  FaSpinner,
  FaUser,
  FaUserMd,
  FaShieldAlt,
} from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";

import {
  sendForgotPasswordRequest,
  clearForgotState,
} from "../../redux/slices/passwords/forgotPasswordSlice";

import {
  sendBusinessForgotPasswordRequest,
  clearBusinessForgotState,
} from "../../redux/slices/passwords/businessForgotPasswordSlice";

const ForgotPassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // ============================================================
  // FORM STATE
  // ============================================================
  const [formData, setFormData] = useState({
    email: "",
    role: "normal",
  });

  const [otpSent, setOtpSent] = useState(false);
  const [validationError, setValidationError] = useState("");

  // ============================================================
  // REDUX STATE
  // ============================================================
  const normalState = useSelector((state) => state.forgotPassword || {});

  const businessState = useSelector(
    (state) => state.businessForgotPassword || {},
  );

  // ============================================================
  // CURRENT STATE
  // ============================================================
  const currentState =
    formData.role === "business" ? businessState : normalState;

  const loading = Boolean(currentState?.loading);
  const message = currentState?.message || "";
  const error = currentState?.error || "";

  // ============================================================
  // CLEAR BOTH REDUX STATES
  // ============================================================
  const clearAllForgotStates = () => {
    dispatch(clearForgotState());
    dispatch(clearBusinessForgotState());
  };

  // ============================================================
  // EMAIL VALIDATION
  // ============================================================
  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());
  };

  // ============================================================
  // INPUT CHANGE
  // ============================================================
  const handleChange = (e) => {
    const { name, value } = e.target;

    setValidationError("");
    setOtpSent(false);
    clearAllForgotStates();

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ============================================================
  // ACCOUNT TYPE TAB CHANGE
  // ============================================================
  const handleRoleChange = (role) => {
    if (loading || otpSent) {
      return;
    }

    console.log("🔄 RESET ACCOUNT TYPE:", role);

    setValidationError("");
    setOtpSent(false);
    clearAllForgotStates();

    setFormData((prev) => ({
      ...prev,
      role,
    }));

    localStorage.setItem("resetRole", role);
  };

  // ============================================================
  // SUBMIT
  // ============================================================
  const handleSubmit = (e) => {
    e.preventDefault();

    if (loading) {
      return;
    }

    const email = formData.email.trim();
    const role = formData.role;

    setValidationError("");

    // EMAIL REQUIRED
    if (!email) {
      setValidationError("Please enter your registered email address.");
      return;
    }

    // EMAIL FORMAT
    if (!validateEmail(email)) {
      setValidationError("Please enter a valid email address.");
      return;
    }

    // VALID ROLE
    if (role !== "normal" && role !== "business") {
      setValidationError("Please select a valid account type.");
      return;
    }

    // CLEAR OLD REQUEST STATE
    clearAllForgotStates();
    setOtpSent(false);

    // SAVE RESET INFORMATION
    localStorage.setItem("resetEmail", email);
    localStorage.setItem("resetRole", role);

    // ==========================================================
    // NORMAL USER BACKEND
    // ==========================================================
    if (role === "normal") {
      console.log("👤 NORMAL USER → /users/normal/forgot-password");

      dispatch(
        sendForgotPasswordRequest({
          email,
        }),
      );

      return;
    }

    // ==========================================================
    // BUSINESS USER BACKEND
    // ==========================================================
    if (role === "business") {
      console.log("🏢 BUSINESS USER → /business/user/forgot-password");

      dispatch(
        sendBusinessForgotPasswordRequest({
          email,
        }),
      );

      return;
    }
  };

  // ============================================================
  // SUCCESS HANDLER
  // ============================================================
  useEffect(() => {
    if (!message) {
      return;
    }

    console.log("✅ OTP SENT SUCCESSFULLY");
    console.log("📩 Message:", message);

    setOtpSent(true);

    const timeout = setTimeout(() => {
      console.log("➡️ Redirecting to /reset-password");
      navigate("/reset-password");
    }, 1500);

    return () => {
      clearTimeout(timeout);
    };
  }, [message, navigate]);

  // ============================================================
  // ERROR HANDLER
  // ============================================================
  useEffect(() => {
    if (!error) {
      return;
    }

    console.error("❌ FORGOT PASSWORD ERROR:", error);

    setOtpSent(false);

    const timer = setTimeout(() => {
      clearAllForgotStates();
    }, 4000);

    return () => {
      clearTimeout(timer);
    };
  }, [error]);

  // ============================================================
  // CLEANUP
  // ============================================================
  useEffect(() => {
    return () => {
      dispatch(clearForgotState());
      dispatch(clearBusinessForgotState());
    };
  }, [dispatch]);

  // ============================================================
  // UI
  // ============================================================
  return (
    <div
      className="
        min-h-screen
        flex
        items-center
        justify-center
        px-4
        py-8
        relative
        overflow-hidden
      "
      style={{
        background:
          "linear-gradient(145deg, rgb(15, 31, 51) 0%, rgb(26, 58, 92) 50%, rgb(30, 73, 118) 100%)",
      }}
    >
      {/* =====================================================
          BACKGROUND DECORATION
      ====================================================== */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-32 -left-32 w-80 h-80 rounded-full bg-cyan-400/10 blur-3xl" />

        <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-blue-500/10 blur-3xl" />

        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-sky-400/5 blur-3xl" />
      </div>

      {/* =====================================================
          MAIN CARD
      ====================================================== */}
      <div
        className="
          relative
          z-10
          w-full
          max-w-md
          overflow-hidden
          rounded-3xl
          border
          border-white/20
          bg-white/95
          shadow-2xl
          backdrop-blur-xl
        "
      >
        {/* ===================================================
            HEADER
        ==================================================== */}
        <div
          className="px-7 pt-8 pb-7 text-center text-white"
          style={{
            background:
              "linear-gradient(145deg, rgb(15, 31, 51) 0%, rgb(26, 58, 92) 50%, rgb(30, 73, 118) 100%)",
          }}
        >
          <div
            className="
              mx-auto
              mb-4
              flex
              h-16
              w-16
              items-center
              justify-center
              rounded-2xl
              border
              border-white/20
              bg-white/10
              shadow-lg
              backdrop-blur-md
            "
          >
            <FaShieldAlt className="text-2xl text-cyan-300" />
          </div>

          <h2 className="text-2xl font-bold tracking-tight">
            Forgot Password?
          </h2>

          <p className="mx-auto mt-2 max-w-xs text-sm leading-6 text-blue-100/80">
            Securely reset your account password using a one-time verification
            code.
          </p>
        </div>

        {/* ===================================================
            FORM CONTENT
        ==================================================== */}
        <div className="p-6 sm:p-8">
          {/* =================================================
              SUCCESS MESSAGE
          ================================================== */}
          {message && (
            <div className="mb-5 rounded-2xl border border-green-200 bg-green-50 p-4 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-green-100">
                  <span className="text-green-600">✓</span>
                </div>

                <div>
                  <p className="text-sm font-semibold text-green-700">
                    OTP Sent Successfully
                  </p>

                  <p className="mt-1 text-xs leading-5 text-green-600">
                    {message}
                  </p>

                  <p className="mt-1 text-xs text-green-600">
                    Redirecting to password reset...
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* =================================================
              ERROR MESSAGE
          ================================================== */}
          {error && (
            <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 p-4 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-red-100">
                  <span className="font-bold text-red-600">!</span>
                </div>

                <div>
                  <p className="text-sm font-semibold text-red-700">
                    Unable to Send OTP
                  </p>

                  <p className="mt-1 text-xs leading-5 text-red-600">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* =================================================
              VALIDATION MESSAGE
          ================================================== */}
          {validationError && (
            <div className="mb-5 rounded-2xl border border-amber-200 bg-amber-50 p-4 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100">
                  <span className="font-bold text-amber-600">!</span>
                </div>

                <p className="pt-1 text-xs leading-5 text-amber-700">
                  {validationError}
                </p>
              </div>
            </div>
          )}

          {/* =================================================
              FORM
          ================================================== */}
          <form onSubmit={handleSubmit}>
            {/* =================================================
                ACCOUNT TYPE TABS
            ================================================== */}
            <div className="mb-6">
              <label className="mb-3 block text-sm font-semibold text-gray-700">
                Select Account Type
              </label>

              <div
                className="
                  grid
                  grid-cols-2
                  gap-2
                  rounded-2xl
                  border
                  border-gray-200
                  bg-gray-100
                  p-1.5
                "
              >
                {/* USER / PATIENT */}
                <button
                  type="button"
                  onClick={() => handleRoleChange("normal")}
                  disabled={loading || otpSent}
                  className={`
                    relative
                    flex
                    min-h-[76px]
                    flex-col
                    items-center
                    justify-center
                    rounded-xl
                    px-3
                    py-3
                    transition-all
                    duration-200
                    ${
                      formData.role === "normal"
                        ? "text-white shadow-lg"
                        : "text-gray-600 hover:bg-white hover:text-gray-800"
                    }
                    ${loading || otpSent ? "cursor-not-allowed opacity-60" : ""}
                  `}
                  style={
                    formData.role === "normal"
                      ? {
                          background:
                            "linear-gradient(135deg, rgb(26, 58, 92), rgb(14, 165, 233))",
                        }
                      : undefined
                  }
                >
                  <FaUser
                    className={`
                      mb-1.5 text-lg
                      ${
                        formData.role === "normal"
                          ? "text-white"
                          : "text-sky-600"
                      }
                    `}
                  />

                  <span className="text-sm font-bold">User / Patient</span>

                  <span
                    className={`
                      mt-0.5 text-[10px]
                      ${
                        formData.role === "normal"
                          ? "text-blue-100"
                          : "text-gray-400"
                      }
                    `}
                  >
                    Personal Account
                  </span>
                </button>

                {/* BUSINESS / DOCTOR */}
                <button
                  type="button"
                  onClick={() => handleRoleChange("business")}
                  disabled={loading || otpSent}
                  className={`
                    relative
                    flex
                    min-h-[76px]
                    flex-col
                    items-center
                    justify-center
                    rounded-xl
                    px-3
                    py-3
                    transition-all
                    duration-200
                    ${
                      formData.role === "business"
                        ? "text-white shadow-lg"
                        : "text-gray-600 hover:bg-white hover:text-gray-800"
                    }
                    ${loading || otpSent ? "cursor-not-allowed opacity-60" : ""}
                  `}
                  style={
                    formData.role === "business"
                      ? {
                          background:
                            "linear-gradient(135deg, rgb(26, 58, 92), rgb(14, 165, 233))",
                        }
                      : undefined
                  }
                >
                  <FaUserMd
                    className={`
                      mb-1.5 text-lg
                      ${
                        formData.role === "business"
                          ? "text-white"
                          : "text-sky-600"
                      }
                    `}
                  />

                  <span className="text-sm font-bold">Business / Doctor</span>

                  <span
                    className={`
                      mt-0.5 text-[10px]
                      ${
                        formData.role === "business"
                          ? "text-blue-100"
                          : "text-gray-400"
                      }
                    `}
                  >
                    Professional Account
                  </span>
                </button>
              </div>

              {/* SELECTED ACCOUNT INDICATOR */}
              <div className="mt-2 text-center">
                <span className="text-xs text-gray-400">
                  Resetting password for{" "}
                </span>

                <span className="text-xs font-semibold text-sky-600">
                  {formData.role === "normal"
                    ? "User / Patient"
                    : "Business / Doctor"}
                </span>
              </div>
            </div>

            {/* =================================================
                EMAIL
            ================================================== */}
            <div className="mb-6">
              <label
                htmlFor="email"
                className="mb-2 block text-sm font-semibold text-gray-700"
              >
                Email Address
              </label>

              <div className="relative">
                <FaEnvelope
                  className="
                    pointer-events-none
                    absolute
                    left-4
                    top-1/2
                    -translate-y-1/2
                    text-gray-400
                  "
                />

                <input
                  id="email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={loading || otpSent}
                  autoComplete="email"
                  placeholder="you@example.com"
                  className="
                    w-full
                    rounded-xl
                    border
                    border-gray-200
                    bg-gray-50
                    py-3.5
                    pl-11
                    pr-4
                    text-sm
                    text-gray-800
                    outline-none
                    transition-all
                    duration-200
                    placeholder:text-gray-400
                    hover:border-gray-300
                    focus:border-sky-500
                    focus:bg-white
                    focus:ring-4
                    focus:ring-sky-100
                    disabled:cursor-not-allowed
                    disabled:bg-gray-100
                  "
                />
              </div>

              <p className="mt-2 text-xs text-gray-400">
                Enter the email address registered with your account.
              </p>
            </div>

            {/* =================================================
                SEND OTP
            ================================================== */}
            <button
              type="submit"
              disabled={loading || otpSent}
              className="
                group
                flex
                w-full
                items-center
                justify-center
                rounded-xl
                py-3.5
                font-semibold
                text-white
                shadow-lg
                transition-all
                duration-200
                hover:-translate-y-0.5
                hover:shadow-xl
                active:translate-y-0
                disabled:cursor-not-allowed
                disabled:opacity-60
                disabled:hover:translate-y-0
              "
              style={{
                background:
                  "linear-gradient(135deg, rgb(26, 58, 92), rgb(14, 165, 233))",
              }}
            >
              {loading ? (
                <>
                  <FaSpinner className="mr-2 animate-spin" />
                  Sending OTP...
                </>
              ) : otpSent ? (
                <>
                  <span className="mr-2">✓</span>
                  OTP Sent
                </>
              ) : (
                <>
                  <FaEnvelope className="mr-2 transition-transform duration-200 group-hover:scale-110" />
                  Send OTP
                </>
              )}
            </button>
          </form>

          {/* =================================================
              SECURITY INFO
          ================================================== */}
          <div className="mt-6 flex items-center justify-center gap-2 text-center">
            <FaShieldAlt className="text-xs text-sky-500" />

            <p className="text-xs text-gray-400">
              Your account information is securely protected.
            </p>
          </div>

          {/* =================================================
              BACK TO LOGIN
          ================================================== */}
          <div className="mt-6 border-t border-gray-100 pt-5 text-center">
            <span className="text-sm text-gray-500">
              Remember your password?{" "}
            </span>

            <Link
              to="/login"
              className="
                text-sm
                font-semibold
                text-sky-600
                transition-colors
                hover:text-blue-700
                hover:underline
              "
            >
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
